import { Check, Infinity, Search, Target, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/controls'
import { Field, Input, Textarea } from '@/components/ui/field'
import { Avatar } from '@/components/ui/display'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/overlays'
import { useQuery } from '@tanstack/react-query'
import { listPeople, peopleSync } from '@/data/people'
import { SPACE_CODE_MAX_LENGTH, suggestCode, type SpaceDraft } from '@/data/spaces'
import type { Space, SpaceKind } from '@/data/types'
import { cn } from '@/lib/utils'

function toDraft(space: Space): SpaceDraft {
  return {
    kind: space.kind,
    name: space.name,
    code: space.code,
    description: space.description,
    members: space.members,
  }
}

const EMPTY: SpaceDraft = { kind: 'project', name: '', code: '', description: '', members: [] }

/**
 * The editor for one space.
 *
 * The code is derived from the name until someone types one, and then it is
 * theirs — a suggestion that keeps overwriting a deliberate choice is worse
 * than no suggestion. It is the field that appears hundreds of times in the
 * meetings list, so it is worth getting right without making anyone think about
 * it on the way in.
 */
export function SpaceEditor({
  space,
  busy,
  onSave,
  onCancel,
}: {
  /** Undefined creates. */
  space?: Space
  busy?: boolean
  onSave: (draft: SpaceDraft) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState<SpaceDraft>(() => (space ? toDraft(space) : EMPTY))
  const [codeTouched, setCodeTouched] = useState(Boolean(space))
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    setDraft(space ? toDraft(space) : EMPTY)
    setCodeTouched(Boolean(space))
    setTouched(false)
  }, [space])

  const set = <K extends keyof SpaceDraft>(key: K, value: SpaceDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  function setName(name: string) {
    setDraft((current) => ({
      ...current,
      name,
      code: codeTouched ? current.code : suggestCode(name, current.kind),
    }))
  }

  const nameError = touched && !draft.name.trim() ? 'A name is required.' : null
  const codeError = touched && !draft.code.trim() ? 'A code is required.' : null

  function save() {
    setTouched(true)
    if (!draft.name.trim() || !draft.code.trim()) return
    onSave({ ...draft, name: draft.name.trim(), code: draft.code.trim() })
  }

  return (
    <div className="space-y-4 border-t border-[var(--color-border)] px-3 py-3">
      {/* Asked FIRST, and only when creating, because it is the one field that
          is awkward to change later: everything filed here inherits it, and the
          two kinds age differently — a project is archived when it closes, an
          area never is. "It finishes." / "It runs on." was true and told nobody
          anything; a sentence and an example do. */}
      {space ? null : <KindChoice value={draft.kind} onChange={(kind) => set('kind', kind)} />}

      <div
        className={cn('grid gap-3', space ? 'sm:grid-cols-[1fr_auto_10rem]' : 'sm:grid-cols-[1fr_auto]')}
      >
        <Field label="Name" required error={nameError}>
          {(props) => (
            <Input
              {...props}
              autoFocus
              value={draft.name}
              maxLength={80}
              placeholder="Project Northwind"
              className="h-8 text-xs"
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        <Field label="Code" required error={codeError} hint="Shown on every meeting row.">
          {(props) => (
            <Input
              {...props}
              value={draft.code}
              maxLength={SPACE_CODE_MAX_LENGTH}
              placeholder="NWD"
              className="h-8 w-24 text-center font-mono text-xs tracking-wide uppercase"
              onChange={(event) => {
                setCodeTouched(true)
                set('code', event.target.value.toUpperCase())
              }}
            />
          )}
        </Field>

        {/* On an existing space this stays a plain control: the choice has
            already been explained once, and re-arguing it every time someone
            renames a deal is noise. */}
        {space ? (
          <Field label="Kind" hint={KINDS[draft.kind].hint}>
            {() => (
              <SegmentedControl
                className="w-full [&>button]:flex-1"
                value={draft.kind}
                onValueChange={(kind) => set('kind', kind)}
                options={[
                  { value: 'project', label: 'Project' },
                  { value: 'area', label: 'Area' },
                ]}
              />
            )}
          </Field>
        ) : null}
      </div>

      <Field label="Description">
        {(props) => (
          <Textarea
            {...props}
            rows={2}
            value={draft.description}
            maxLength={400}
            placeholder="What this space is for, in a sentence."
            className="text-xs leading-relaxed"
            onChange={(event) => set('description', event.target.value)}
          />
        )}
      </Field>

      <div>
        <p className="mb-1.5 text-xs font-medium text-[var(--color-fg-muted)]">People</p>
        <MemberPicker
          members={draft.members}
          onChange={(members) => set('members', members)}
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" loading={busy} onClick={save}>
          {space ? 'Save changes' : 'Create space'}
        </Button>
      </div>
    </div>
  )
}

const KINDS: Record<
  SpaceKind,
  { label: string; hint: string; body: string; example: string; icon: typeof Target }
> = {
  project: {
    label: 'Project',
    hint: 'It finishes.',
    body: 'A piece of work with an end. It closes, and you archive it — the meetings stay, filed and searchable, but it stops being offered when you file something new.',
    example: 'A deal, a mandate, a diligence, a refinancing.',
    icon: Target,
  },
  area: {
    label: 'Area',
    hint: 'It runs on.',
    body: 'A standing responsibility with no end date. It keeps collecting meetings for as long as the relationship lasts, so it is never archived in the normal course.',
    example: 'A client account, a portfolio company, your own internal meetings.',
    icon: Infinity,
  },
}

/**
 * Project or area, explained rather than named.
 *
 * The distinction decides whether the space is ever archived, and nothing on
 * the form says so: the two words look like a taxonomy someone has to already
 * know, and picking wrong is only discovered months later when a finished deal
 * is still in every filing list. Two cards cost one row of vertical space on
 * the one screen where the answer is not yet obvious — creating the space —
 * and buy a decision made on what actually differs.
 */
function KindChoice({
  value,
  onChange,
}: {
  value: SpaceKind
  onChange: (kind: SpaceKind) => void
}) {
  return (
    <div role="radiogroup" aria-label="Kind" className="grid gap-2 sm:grid-cols-2">
      {(Object.keys(KINDS) as SpaceKind[]).map((kind) => {
        const spec = KINDS[kind]
        const Icon = spec.icon
        const active = kind === value
        return (
          <button
            key={kind}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(kind)}
            className={cn(
              'rounded-[var(--radius-md)] border p-2.5 text-left transition-colors duration-100 outline-none',
              'focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35',
              active
                ? 'border-[var(--color-primary)] bg-[var(--color-surface)]'
                : 'border-[var(--color-border)] bg-[var(--color-surface-sunken)] hover:border-[var(--color-border-strong)]'
            )}
          >
            <span className="flex items-center gap-1.5">
              <Icon
                className={cn(
                  'size-3.5',
                  active ? 'text-[var(--color-primary)]' : 'text-[var(--color-fg-subtle)]'
                )}
              />
              <span className="text-xs font-medium text-[var(--color-fg)]">{spec.label}</span>
              {active ? (
                <Check className="ml-auto size-3.5 text-[var(--color-primary)]" />
              ) : null}
            </span>
            <span className="mt-1 block text-2xs leading-relaxed text-[var(--color-fg-muted)]">
              {spec.body}
            </span>
            <span className="mt-1 block text-2xs text-[var(--color-fg-subtle)]">
              {spec.example}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Who the space is about — not who can see it.
 *
 * Access is decided per meeting, on the meeting row, and duplicating it here
 * would create a second answer to "can Rachel read this" that nothing
 * reconciles. This list is context: it is what the chat uses to resolve "what
 * did the client say" without being told who the client is.
 */
function MemberPicker({
  members,
  onChange,
}: {
  members: string[]
  onChange: (members: string[]) => void
}) {
  const [query, setQuery] = useState('')
  const normalized = query.trim().toLowerCase()
  const directory = useQuery({ queryKey: ['people'], queryFn: listPeople }).data ?? peopleSync()
  const visible = directory.filter(
    (person) =>
      !normalized ||
      person.name.toLowerCase().includes(normalized) ||
      person.email.toLowerCase().includes(normalized)
  )

  const toggle = (email: string) =>
    onChange(members.includes(email) ? members.filter((item) => item !== email) : [...members, email])

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {members.map((email) => {
        const person = directory.find((item) => item.email === email)
        return (
          <span
            key={email}
            className="inline-flex h-6 items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface)] pr-1 pl-1.5 text-2xs text-[var(--color-fg)]"
          >
            <Avatar name={person?.name ?? email} size="xs" />
            <span className="max-w-40 truncate">{person?.name ?? email}</span>
            {person && !person.isInternal ? (
              <span className="text-[var(--color-fg-subtle)]">ext</span>
            ) : null}
            <button
              type="button"
              onClick={() => toggle(email)}
              aria-label={`Remove ${person?.name ?? email}`}
              className="grid size-4 place-items-center rounded-[var(--radius-xs)] text-[var(--color-fg-subtle)] outline-none hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]"
            >
              <X className="size-2.5" />
            </button>
          </span>
        )
      })}

      <Popover onOpenChange={(open) => !open && setQuery('')}>
        <PopoverTrigger
          className={cn(
            'inline-flex h-6 items-center gap-1 rounded-[var(--radius-sm)] border border-dashed px-2 text-2xs outline-none',
            'border-[var(--color-border)] text-[var(--color-fg-muted)] hover:border-[var(--color-border-strong)] hover:text-[var(--color-fg)]'
          )}
        >
          Add person
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <div className="relative border-b border-[var(--color-border)]">
            <Search className="absolute top-1/2 left-2.5 size-3 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
            <Input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search people…"
              aria-label="Search people"
              className="h-8 rounded-none border-0 pl-7 text-xs focus-visible:ring-0"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto p-1">
            {visible.map((person) => (
              <li key={person.email}>
                <button
                  type="button"
                  onClick={() => toggle(person.email)}
                  className="flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-left hover:bg-[var(--color-surface-hover)]"
                >
                  <Avatar name={person.name} size="xs" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs text-[var(--color-fg)]">
                      {person.name}
                    </span>
                    <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
                      {person.email}
                    </span>
                  </span>
                  {members.includes(person.email) ? (
                    <Check className="size-3.5 shrink-0 text-[var(--color-primary)]" />
                  ) : null}
                </button>
              </li>
            ))}
            {visible.length === 0 ? (
              <li className="px-2 py-6 text-center text-2xs text-[var(--color-fg-subtle)]">
                No matches.
              </li>
            ) : null}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  )
}
